<template>
    <div class="mt-5 w-full max-w-7xl">
        <!-- Wallet header with arrows and dropdown -->
        <div class="w-full mt-5">
            <div class="w-full flex items-center gap-3">
                <!-- Left arrow box -->
                <button type="button"
                    :class="['w-[40px] h-[124px] bg-white border border-content-basic-4 rounded-02 flex items-center justify-center shrink-0', canPrev ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed']"
                    :disabled="!canPrev"
                    @click="prevWallet">
                    <ChevronLeftIcon :width="36" :height="36" color="text-content-basic-2" weight="REGULAR" />
                </button>

                <!-- Blue wallet bar (center) -->
                <div class="relative flex-1 w-[936px] h-[124px] bg-primary-3 rounded-02 flex items-center justify-center px-6">
                    <div class="flex flex-col items-center">
                        <div class="relative">
                            <button type="button"
                                class="text-white font-inter-heading-14 flex items-center gap-2" 
                                @click="toggleWalletDropdown">
                                    {{ selectedWallet.walletCategoryName }}
                                <span class="inline-flex items-center justify-center w-[24px] h-[24px] rounded-full bg-[#364EBE]">
                                    <ArrowDropDownIcon
                                        :width="16"
                                        :height="16"
                                        color="text-white"
                                        weight="REGULAR"
                                        :class="[showWalletDropdown ? 'rotate-180' : '', 'transition-transform', 'block']" />
                                </span>
                            </button>
                            <div v-if="showWalletDropdown" class="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20 w-72">
                                <div class="absolute left-0 top-0 w-full max-h-60 overflow-y-auto bg-background-basic-1 border border-border-basic-2 rounded-01_5 shadow ds-scrollbar">
                                    <button
                                        v-for="w in wallets"
                                        :key="w.walletCategorySeq"
                                        type="button"
                                        class="w-full text-left"
                                        @click="selectWallet(w.walletCategorySeq)"
                                    >
                                        <div class="px-3 py-3 hover:bg-background-basic-2">
                                            <span class="font-inter-heading-20 text-content-basic-1">{{ w.walletCategoryName }}</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="text-white font-inter-heading-1 mt-2">{{ numberWithCommas(selectedWallet.subAdminAvailableMileage) }} Points</div>
                    </div>
                </div>

                <!-- Right arrow box -->
                <button type="button"
                    :class="['w-[40px] h-[124px] bg-white border border-content-basic-4 rounded-02 flex items-center justify-center shrink-0', canNext ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed']"
                    :disabled="!canNext"
                    @click="nextWallet">
                    <ChevronRightIcon :width="36" :height="36" color="text-content-basic-2" weight="REGULAR" />
                </button>
            </div>
        </div>

        <!-- Recipient Department -->
         <div class="mt-5 p-10 flex flex-col gap-10 bg-white border border-content-basic-4 rounded-02">
            <div>
                <div class="font-inter-heading-15 text-content-basic-1 mb-2">Recipient Department</div>
                <div class="font-inter-body-2 text-content-basic-3 mb-4">Points are distributed equally to all employees in the selected department(s).</div>

                <div class="flex flex-col gap-6">
                    <div v-for="(row, idx) in departmentRows" :key="row.departmentSeq || idx" class="border border-content-basic-4 rounded-02">
                        <div class="flex items-stretch h-full">
                            <div class="relative w-11 flex items-center justify-center self-stretch mr-4">
                                <span class="absolute right-0 top-3 bottom-3 w-px bg-background-basic-3"></span>
                                <div class="font-inter-heading-13 text-content-basic-2 text-center">{{ idx + 1 }}</div>
                            </div>
                            <div class="flex-1 px-2 py-5 flex flex-col gap-4">
                                <div>
                                    <div class="font-inter-body-2 mb-1">Department</div>
                                    <DesignDropdownSelect
                                        v-model="row.departmentSeq"
                                        :options="departmentOptions"
                                        size="LG"
                                        :width="'w-80'"
                                        placeholder="Select Recipient Department"
                                        :useDefaultMsg="false"
                                        :error="row.departmentErrors"
                                        @change="onChangeDepartment(idx)" />
                                </div>
                                <div>
                                    <div class="font-inter-body-2 mb-1">Points per person</div>
                                    <div class="flex items-center gap-2">
                                        <DesignInput
                                            v-model="row.mileagePerPerson"
                                            placeholder="0"
                                            size="LG"
                                            :width="'w-80'"
                                            :error="row.pointsErrors"
                                            @update:modelValue="onInputPoints($event, idx)"
                                        />
                                        <span class="font-body-sm-regular text-content-basic-1">
                                            P
                                        </span>
                                    </div>
                                </div>
                                <div class="grid grid-cols-2 font-inter-heading-19 text-content-basic-1 border-t border-background-basic-3 mt-4 pt-4">
                                    <div class="justify-self-start">
                                        Employees <span class=" font-inter-heading-14">{{ row.personCount ?? '-' }}</span>
                                    </div>
                                    <div class="justify-self-start">
                                        Total <span class=" font-inter-heading-14">{{ row.totalMileageVolume != null ? numberWithCommas(row.totalMileageVolume) : '-' }}</span>
                                    </div>
                                </div>
                            </div>
                            <!-- Delete icon column -->
                            <div class="w-10 flex items-start justify-end pt-2 p-2" v-show="idx !== 0">
                                <button type="button" @click="askDelete(idx)" class="text-primary-3 hover:text-primary-2">
                                    <DeleteIcon :width="35" :height="35" color="text-[#7482BE]" weight="REGULAR" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-center">
                        <DesignButton 
                        btnSize="MD" 
                        btnType="QUATERNARY" 
                        :btnDisabled="false" 
                        btnText="+ Add Department" 
                        @click-event="addDepartment" 
                        :style="{ width: '320px', height: '48px' }"
                        :class="'text-primary-3 border border-primary-3'" />
                    </div>
                </div>
            </div>

            <!-- Message -->
            <div>
                <div class="font-inter-heading-15 text-content-basic-1 mb-2">Message</div>
                <textarea 
                    v-model="transactionMessage" 
                    placeholder="Message" 
                    maxlength="150"
                    @input="onMessageInput($event.target.value)"
                    :class="['p-5 outline-0 w-full h-[114px] text-sm rounded-lg bg-white transition-all duration-500 shadow shadow-gray-100', showTransactionMessageError ? 'border-accent-red-3 border' : 'border']" />
                <p v-if="showTransactionMessageError" class="mt-2 font-inter-caption-2 text-accent-red-3">This field is required.</p>
            </div>

            <!-- Totals + Submit -->
            <div>
                <div class="font-inter-heading-15 text-content-basic-1">Total</div>
                <div class="mt-4 flex items-baseline gap-16">
                    <div class="flex items-baseline gap-3">
                        <span class="font-inter-heading-19 text-content-basic-1">Departments</span>
                        <span class="font-inter-heading-19 text-primary-3">{{ departmentRows.length }}</span>
                    </div>
                    <div class="flex items-baseline gap-3">
                        <span class="font-inter-heading-19 text-content-basic-1">Employees</span>
                        <span class="font-inter-heading-19 text-primary-3">{{ totalEmployees || '-' }}</span>
                    </div>
                    <div class="flex items-baseline gap-3">
                        <span class="font-inter-heading-19 text-content-basic-1">Total Points</span>
                        <span class="font-inter-heading-19 text-primary-3">{{ totalPoints > 0 ? numberWithCommas(totalPoints) : '-' }}</span>
                    </div>
                </div>
                <button @click="clickTransferButton" v-if="isTransfer" class="rounded-[12px] mt-8 w-full text-center text-[#fff] h-[62px] leading-[62px] bg-[#4640DE]">
                    Transfer
                </button>
                <button @click="clickDeductButton" v-if="isDeduct" class="rounded-[12px] mt-8 w-full text-center text-[#fff] h-[62px] leading-[62px] bg-[#4640DE]">
                    Deduct
                </button>
            </div>
         </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
        <DesignConfirmPopup
            v-model="showAlertPopup"
            :showTitle="true"
            title="Failed"
            :confirmDescription="alertMessage"
            buttonText="OK"
            confirmBtnType="RED_SOLID"
            :showCancel="false"
        />
        <DesignConfirmPopup
            v-model="showConfirmPopup"
            :showTitle="true"
            title="Confirm"
            :confirmDescription="confirmMessage"
            buttonText="Yes"
            :showCancel="true"
            cancelText="Cancel"
            :hasAfterConfirm="true"
            @after-confirm="clickSubmit"
        />
        <DesignConfirmPopup
            v-model="showDeleteConfirm"
            :showTitle="true"
            title="Delete Confirmation"
            confirmDescription="Are you sure you want to delete this department?"
            :showCancel="true"
            cancelText="Cancel"
            buttonText="Delete"
            confirmBtnType="RED_SOLID"
            cancelBtnType="QUATERNARY"
            :hasAfterConfirm="true"
            @after-confirm="confirmDelete"
            @cancel="cancelDelete()"
        />
    </Teleport>
</template>

<script>
import DesignDropdownSelect from '@/components/design-system/design-dropdown-select.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import DesignInput from '@/components/design-system/design-input.vue';
import DesignConfirmPopup from '@/components/design-system/design-confirm-popup.vue';
import DeleteIcon from '@/components/design-system/icon/delete.vue';
import ChevronLeftIcon from '@/components/design-system/icon/chevron-left.vue';
import ChevronRightIcon from '@/components/design-system/icon/chevron-right.vue';
import ArrowDropDownIcon from '@/components/design-system/icon/arrow-drop-down.vue';

export default {
    components: {
        DesignDropdownSelect,
         DesignButton, 
         DesignInput,
         ChevronLeftIcon, 
         ChevronRightIcon,
         ArrowDropDownIcon,
         DeleteIcon,
         DesignConfirmPopup,
    },
    props: {
        transactionType: String,
        // 세금 적용 여부 (Shell/호출 측에서 제어)
        taxApplied: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submitTransferToDepartment', 'submitDeductToDepartment'],
    data() {
        return {
            maxAvailablePoints: 9999999999999999.99,
            maxAvailablePointsFormatted: '9,999,999,999,999,999.99',
            wallets: [],
            walletIndex: 0,
            selectedWallet: {
                walletCategorySeq: null,
                walletCategoryName: null,
                subAdminAvailableMileage: 0,
            },
            showWalletDropdown: false,

            departmentOptions: [],
            departments: [],
            departmentRows: [
                {
                    departmentSeq: null,
                    departmentName: null,
                    personCount: 0,
                    mileagePerPerson: '0',
                    totalMileageVolume: 0,
                    departmentErrors: "",
                    pointsErrors: "",
                }
            ],
            transactionMessage: '',
            showTransactionMessageError: false,
            showDeleteConfirm: false,
            showAlertPopup: false,
            alertMessage: '',
            deleteTargetIndex: null,

            showConfirmPopup: false,
            confirmMessage: '',
            hasRunValidation: false,
        };
    },
    computed: {
        isTransfer() { return this.transactionType === 'transfer'; },
        canPrev() { return (this.wallets?.length || 0) > 0 && this.walletIndex > 0; },
        canNext() { return (this.wallets?.length || 0) > 0 && this.walletIndex < (this.wallets.length - 1); },
        isDeduct() { return this.transactionType === 'deduct'; },
        totalEmployees() {
            return this.departmentRows.reduce((sum, r) => sum + (Number(r.personCount) || 0), 0);
        },
        totalPoints() {
            return this.departmentRows.reduce((sum, r) => sum + (Number(r.totalMileageVolume) || 0), 0);
        },
    },
    mounted() {
        this.getWallets().then(() => {
            if (!this.selectedWallet?.walletCategorySeq && this.wallets.length > 0) {
                this.selectWallet(this.wallets[0].walletCategorySeq);
            }
        });
        this.getDepartments();
    },
    watch: {
        transactionType() {
            this.departmentRows = [
                {
                    departmentSeq: null,
                    departmentName: null,
                    personCount: 0,
                    mileagePerPerson: '0',
                    totalMileageVolume: 0,
                    departmentErrors: '',
                    pointsErrors: '',
                }
            ];

            this.transactionMessage = '';
            this.showTransactionMessageError = false;

            this.getWallets();
            this.getDepartments();
        },
    },
    methods: {
        askDelete(idx) {
            this.deleteTargetIndex = idx;
            this.showDeleteConfirm = true;
        },
        cancelDelete() {
            this.showDeleteConfirm = false;
            this.deleteTargetIndex = null;
        },
        confirmDelete() {
            if (this.deleteTargetIndex != null) {
                this.departmentRows.splice(this.deleteTargetIndex, 1);
            }

            // department duplicate check
            const seqCount = this.departmentRows.reduce((acc, r) => {
                const seq = r && r.departmentSeq;
                if (seq) acc[seq] = (acc[seq] || 0) + 1;
                return acc;
            }, {});
            this.departmentRows.forEach(r => {
                const seq = r && r.departmentSeq;
                if (seq && seqCount[seq] > 1) {
                    r.departmentErrors = 'This department is already added in the list.';
                } else if (seq) {
                    r.departmentErrors = '';
                }
            });

            // re-validate all rows
            this.validateAllPoints();
            
            this.showDeleteConfirm = false;
            this.deleteTargetIndex = null;
        },
        createEmptyRow() {
            return {
                uid: Math.random().toString(36).slice(2),
                departmentSeq: null,
                DepartmentName: null,
                personCount: 0,
                mileagePerPerson: '0',
                totalMileageVolume: 0,
                errors: { department: null, points: null },
            };
        },
        toggleWalletDropdown() { this.showWalletDropdown = !this.showWalletDropdown; },
        prevWallet() {
            if (this.wallets.length === 0 || this.walletIndex <= 0) return;
            this.walletIndex = this.walletIndex - 1;
            this.selectWallet(this.wallets[this.walletIndex].walletCategorySeq);
        },
        nextWallet() {
            if (this.wallets.length === 0 || this.walletIndex >= this.wallets.length - 1) return;
            this.walletIndex = this.walletIndex + 1;
            this.selectWallet(this.wallets[this.walletIndex].walletCategorySeq);
        },
        selectWallet(seq) {
            const list = this.wallets || [];
            const idx = list.findIndex(o => o?.walletCategorySeq === seq);
            const found = idx >= 0 ? list[idx] : null;
            this.selectedWallet = {
                walletCategorySeq: found?.walletCategorySeq || null,
                walletCategoryName: found?.walletCategoryName || null,
                subAdminAvailableMileage: Number(found?.walletSubAdminMileage) || 0,
            };
            if (idx >= 0) this.walletIndex = idx;
            
            this.validateAllPoints();
            this.showWalletDropdown = false;
        },
        getWallets() {
            const url = this.$api('uri', 'get-sub-admin-wallets');
            return this.$axios.get(url).then(res => {
                this.wallets = res.data?.data?.list || [];

                // 첫 번째 항목 기본 선택
                if (this.wallets && this.wallets.length > 0) {
                    this.selectWallet(this.wallets[0].walletCategorySeq);
                }
            }).catch(err => console.error('get Wallets error', err));
        },
        getDepartments() {
            const url = this.$api('uri', 'get-sub-admin-departments');
            const params = { includeResigned: false};
            this.$axios.get(url, { params })
            .then(res => {
                this.departments = res.data?.data?.list || [];
                this.departmentOptions = this.departments.map(dep => ({
                    text: dep.departmentName,
                    value: dep.departmentSeq,
                }));
            })
            .catch(err => console.error('getDepartments error', err));
        },
        onChangeDepartment(idx) {
            const row = this.departmentRows[idx];
            const department = this.departments.find(d => d.departmentSeq === row.departmentSeq);

            row.departmentName = department?.departmentName;
            row.personCount = department?.employeeCount || 0;
            row.departmentErrors = '';

            // duplicated department check within current set
            const duplicated = this.departmentRows.some((r, i) => i !== idx && r.departmentSeq && r.departmentSeq === row.departmentSeq);
            if (duplicated) {
                row.departmentErrors = 'This department is already added in the list.';
            }

            this.validatePoint(idx);
        },
        onInputPoints(eventOrValue, idx) {
            const raw = (typeof eventOrValue === 'string'
                ? eventOrValue
                : (eventOrValue && eventOrValue.target && eventOrValue.target.value) || '') || '';
            const digits = raw.replace(/[^0-9]/g, '').replace(/^0+(?!$)/, '');

            const row = this.departmentRows[idx];
            row.mileagePerPerson = digits ? this.numberWithCommas(digits) : '';
            this.validatePoint(idx);
        },
        onMessageInput(val) {
            // validation을 한번도 실행하지 않은 경우에는 Message 필수입력 오류 메세지 보여지지 않도록 함
            if (!this.hasRunValidation) {
                return;
            }
            this.showTransactionMessageError = !String(val).trim();
        },
        openAlertPopup(message) {
            this.alertMessage = message;
            this.showAlertPopup = true;
        },
        openConfirmPopup(message) {
            this.confirmMessage = message;
            this.showConfirmPopup = true;
        },
        validatePoint(idx) {
            this.isTransfer ? this.validateTransferDepartmentPoint(idx) : this.validateDeductDepartmentPoint(idx);
        },
        validateAllPoints() {
            this.isTransfer ? this.validateTransferAllDepartmentsPoints() : this.validateDeductAllDepartmentsPoints();
        },
        validateTransferDepartmentPoint(idx) {
            const row = this.departmentRows[idx];
            const personCount = Number(row.personCount) || 0;
            const mileagePerPerson = Number((row.mileagePerPerson || '0').toString().replace(/,/g, '')) || 0;
            row.totalMileageVolume = personCount * mileagePerPerson;
            
            // 남은 포인트가 부서의 포인트보다 작은 경우 체크
            row.pointsErrors = '';
            const remainingPoints = Math.max(0, this.selectedWallet.subAdminAvailableMileage - this.totalPoints + row.totalMileageVolume);
            if (remainingPoints < row.totalMileageVolume) {
                row.pointsErrors = 'Insufficient points.';
            }
        },
        validateTransferAllDepartmentsPoints() {
            for (let i = 0; i < this.departmentRows.length; i++) {
                this.validateTransferDepartmentPoint(i);
            }
        },
        validateDeductDepartmentPoint(idx) {
            const row = this.departmentRows[idx];
            const personCount = Number(row.personCount) || 0;
            const mileagePerPerson = Number((row.mileagePerPerson || '0').toString().replace(/,/g, '')) || 0;
            row.totalMileageVolume = personCount * mileagePerPerson;

            row.pointsErrors = '';
            if (row.totalMileageVolume > this.maxAvailablePoints) {
                row.pointsErrors = `The maximum deductable points is ${this.maxAvailablePointsFormatted}.`;
            }
        },
        validateDeductAllDepartmentsPoints() {
            for (let i = 0; i < this.departmentRows.length; i++) {
                this.validateDeductDepartmentPoint(i);
            }
        },
        hasDuplicateDepartments() {
            const seen = new Set();
            return this.departmentRows.some(r => {
                const seq = r && r.departmentSeq;
                if (!seq) return false;
                if (seen.has(seq)) return true;
                seen.add(seq);
                return false;
            });
        },
        getSelectedDepartmentSeqList() {
            return this.departmentRows
                .map(r => r && r.departmentSeq)
                .filter(seq => !!seq);
        },
        addDepartment() {
            this.departmentRows.push({
                departmentSeq: null,
                departmentName: null,
                personCount: 0,
                mileagePerPerson: 0,
                totalMileageVolume: 0,
                departmentErrors: '',
                pointsErrors: '',
            });
        },
        numberWithCommas(number) {
            if (!number) return '0';
            return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        checkValidation() {
            this.hasRunValidation = true;
            let invalid = 0;
            this.showTransactionMessageError = false;

            if ((this.transactionMessage || '').trim().length === 0) {
                this.showTransactionMessageError = true;
                invalid++;
            }

            this.departmentRows.forEach(r => {
                if (!r.departmentSeq) {
                    r.departmentErrors = 'Please select a department.'; 
                    invalid++;
                }

                const perPerson = Number((r.mileagePerPerson || '0').toString().replace(/,/g, ''));
                r.pointsErrors = '';

                // 세금 적용 시에는 최소 2 포인트 이상이어야 함
                if (this.isTransfer && this.taxApplied && (!Number.isFinite(perPerson) || perPerson < 2)) {
                    r.pointsErrors = 'Please enter at least 2 points per person.';
                    invalid++;
                } else if (!Number.isFinite(perPerson) || perPerson < 1) {
                    // 기본 검증: 1 미만이거나 숫자가 아니면 오류
                    r.pointsErrors = 'Please enter a valid whole number.';
                    invalid++;
                }
            });

            // 거래 포인트가 0이하인 경우 체크
            if (this.totalPoints <= 0) {
                invalid++;
            }

            // 최대 거래 가능 포인트가 초과된 경우 체크
            if (this.totalPoints > this.maxAvailablePoints) {
                invalid++;
            }

            // Transfer인 경우 총 부서의 포인트가 wallet에 있는 포인트보다 큰 경우 체크
            if (this.isTransfer) {
                if (this.totalPoints > this.selectedWallet.subAdminAvailableMileage) {
                    invalid++;
                }
            }

            // 부서 중복 체크
            if (this.hasDuplicateDepartments()) {
                invalid++;
            }

            return invalid;
        },
        clickTransferButton() {
            this.hideConfirmPopup();

            if (this.checkValidation() != 0) {
                this.openAlertPopup('Transfer failed due to an invalid input. Please check and try again.');
                return;
            }

            this.openConfirmPopup('Are you sure you want to transfer points?');
            
        },
        clickDeductButton() {
            this.hideConfirmPopup();

            if (this.checkValidation() != 0) {
                this.openAlertPopup('Deduct failed due to an invalid input. Please check and try again.');
                return;
            }

            this.openConfirmPopup('Are you sure you want to deduct points?');
        },
        clickSubmit() {
            this.hideConfirmPopup();

            if (this.isTransfer) {
                this.submitTransfer();
            } else if (this.isDeduct) {
                this.submitDeduct();
            }
        },
        submitTransfer() {
            const departmentTransactionForms = this.departmentRows.map(r => ({
                departmentSeq: r.departmentSeq,
                departmentName: r.departmentName,
                mileagePerPerson: Number((r.mileagePerPerson || '0').toString().replace(/,/g, '')),
                personCount: Number(r.personCount) || 0,
                totalMileageVolume: Number(r.totalMileageVolume) || 0,
            }));

            const payload = {
                walletCategorySeq: this.selectedWallet.walletCategorySeq,
                walletCategoryName: this.selectedWallet.walletCategoryName,
                transactionNote: (this.transactionMessage || '').trim(),
                totalDepartmentCount: this.departmentRows.length,
                totalPersonCount: this.totalEmployees,
                totalMileageVolume: Number(this.totalPoints) || 0,
                departmentTransactionForms,
                taxAppliedYN: this.taxApplied? 'Y' : 'N',
            };
            this.$emit('submitTransferToDepartment', payload);
            
        },
        submitDeduct() {
            const departmentTransactionForms = this.departmentRows.map(r => ({
                departmentSeq: r.departmentSeq,
                departmentName: r.departmentName,
                mileagePerPerson: Number((r.mileagePerPerson || '0').toString().replace(/,/g, '')),
                personCount: Number(r.personCount) || 0,
                totalMileageVolume: Number(r.totalMileageVolume) || 0,
            }));

            const payload = {
                walletCategorySeq: this.selectedWallet.walletCategorySeq,
                walletCategoryName: this.selectedWallet.walletCategoryName,
                transactionNote: (this.transactionMessage || '').trim(),
                totalDepartmentCount: this.departmentRows.length,
                totalPersonCount: this.totalEmployees,
                totalMileageVolume: Number(this.totalPoints) || 0,
                departmentTransactionForms,
            };
            this.$emit('submitDeductToDepartment', payload);
            
        },
        hideConfirmPopup() {
            this.showConfirmPopup = false;
        },
        resetAfterSubmit() {
            // Reset message and rows
            this.transactionMessage = '';
            this.showTransactionMessageError = false;
            this.departmentRows = [
                {
                    departmentSeq: null,
                    departmentName: null,
                    personCount: 0,
                    mileagePerPerson: '0',
                    totalMileageVolume: 0,
                    departmentErrors: '',
                    pointsErrors: '',
                },
            ];

            // Refresh wallets and select first
            this.getWallets().then(() => {
                if (this.wallets && this.wallets.length > 0) {
                    this.selectWallet(this.wallets[0].walletCategorySeq);
                }
            });
            // Refresh departments and summary for deduct validation
            this.getDepartments();
        },
    },
};
</script>
